import { put, select, takeEvery } from 'redux-saga/effects'

import {
  LOCATION_FETCH_FAIL,
  LOCATION_FETCHING,
  LOCATION_PERMISSION_CHECK,
  LOCATION_PERMISSION_STATUS_SET,
  LOCATION_SET,
  SET_TIP_DATA,
  SET_TIP_LOCATION_TOGGLE,
  UPDATE_LOADING,
  FETCH_ELECTRON_LOCALE,
  FETCH_ELECTRON_OS_USER
} from '@redux/actions/types'

import { LOCATION_STATUS } from '@utils/helpers'

import {
  browserName,
  isIE,
  isIOS,
  isMobile,
  isSafari
} from 'react-device-detect'

import { SET_ELECTRON } from '../actions/types'

const getIsElectron = state => state.location.isElectron

const getSendLocation = state => state.tip.sendLocation

const getUserLocation = () => new Promise((resolve, reject) => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      location => resolve(location),
      error => reject(error),
      { timeout: 10000 }
    )
  } else {
    reject(new Error('Location not supported'))
  }
})


/**
 * Fetches the Electron app locale and stores it in Redux state.
 */
function * fetchElectronLocale() {
  const electronLocale = yield window.api.getElectronLocale()

  yield put({
    type: SET_ELECTRON,
    payload: { electronLocale }
  })
}

/**
 * Fetches the Electron OS username and stores it in Redux state.
 */
function * fetchElectronOSUser() {
  const electronOSUser = yield window.api.getElectronOSUsername()

  yield put({
    type: SET_ELECTRON,
    payload: { electronOSUser }
  })
}

/**
 * Handles geolocation fetch, stores coordinates, and dispatches success or error actions.
 * @param action
 */
function * onLocationFetch(action) {
  const successType = action?.payload
  yield put({
    type: UPDATE_LOADING,
    payload: {
      loadingType: LOCATION_FETCHING,
      loadingAction: 'set',
      meta: null
    }
  })

  try {
    const location = yield getUserLocation()

    const { latitude, longitude } = location.coords

    yield put({
      type: LOCATION_SET,
      payload: {
        currentLocation: { latitude, longitude }
      }
    })
    
    yield put({
      type: SET_TIP_DATA,
      payload: {
        sendLocation: true
      }
    })

    if (successType) {
      yield put({ type: successType })
    }
  } catch (error) {
    /*
     * DEV-5041: suppress the "Unable to retrieve your location" and
     * "Location permissions were denied" toasts that used to pile up
     * on top of Chrome's native permission UI. The browser's own
     * prompt / "Location blocked" affordance is the canonical surface
     * for permission state, and the Emergency confirmation modal /
     * Tip location toggle already communicate location-missing state
     * inline — the toasts added noise without adding signal.
     */
    yield put({
      type: SET_TIP_DATA,
      payload: {
        sendLocation: false
      }
    })

    yield put({
      type: UPDATE_LOADING,
      payload: {
        loadingType: LOCATION_FETCHING,
        loadingAction: 'unset',
        meta: null
      }
    })

    yield put({
      type: UPDATE_LOADING,
      payload: {
        loadingAction: 'unset',
        meta: null
      }
    })

    yield put({ type: LOCATION_FETCH_FAIL })
  }

  yield put({
    type: UPDATE_LOADING,
    payload: {
      loadingType: LOCATION_FETCHING,
      loadingAction: 'unset',
      meta: null
    }
  })
}

/**
 * Checks the browser geolocation permission status and updates Redux state accordingly.
 */
function * onLocationPermissionCheck() {
  const isGoogleBrowser = browserName === 'Chrome' || 'GSA'

  const isIOSChrome = isIOS && isMobile && isGoogleBrowser

  const permissionsNotAvailable = isIE || isSafari || isIOSChrome

  const isElectron = yield select(getIsElectron)

  if (isElectron) {
    yield put({
      type: LOCATION_PERMISSION_STATUS_SET,
      payload: LOCATION_STATUS.GRANTED
    })
    return
  }

  if (permissionsNotAvailable) {
    yield put({
      type: LOCATION_PERMISSION_STATUS_SET,
      payload: LOCATION_STATUS.DENIED
    })
  } else {
    const result = yield navigator.permissions.query({ name: 'geolocation' })

    switch (result.state) {
    case 'granted':
      yield put({
        type: LOCATION_PERMISSION_STATUS_SET,
        payload: LOCATION_STATUS.GRANTED
      })
      break
    default:
      yield put({
        type: LOCATION_PERMISSION_STATUS_SET,
        payload: LOCATION_STATUS.DENIED
      })
      break
    }
  }
}

/**
 * Toggles the send-location flag for tips, fetching location if enabling.
 */
function * onSetTipLocationToggle() {
  const sendLocation = yield select(getSendLocation)

  if (sendLocation) {
    yield put({
      type: SET_TIP_DATA,
      payload: {
        sendLocation: false
      }
    })
  } else {
    yield put({ type: LOCATION_FETCHING })
  }
}

/**
 * Root saga that watches for location-related actions.
 */
function * watchLocation() {
  yield takeEvery(LOCATION_FETCHING, onLocationFetch)
  yield takeEvery(FETCH_ELECTRON_LOCALE, fetchElectronLocale)
  yield takeEvery(FETCH_ELECTRON_OS_USER, fetchElectronOSUser)
  yield takeEvery(LOCATION_PERMISSION_CHECK, onLocationPermissionCheck)
  yield takeEvery(SET_TIP_LOCATION_TOGGLE, onSetTipLocationToggle)
}

export default watchLocation
